import { CustomerResponse } from '../types/customer';
import { ConstructionSiteRequest } from '../types/construction-site';
import {
  CONSTRUCTION_SITE_FORM_FIELDS,
  ConstructionSiteFormValue,
  EMPTY_CONSTRUCTION_SITE_FORM_VALUE,
} from '../types/construction-site-form';

/** Create body: every non-empty field. Empty ones are omitted — omitted and `null`
 *  are equivalent for the backend, and an empty string would trip the
 *  minimum-length validation of a field configured as mandatory. */
export function toCreateRequest(
  value: ConstructionSiteFormValue
): ConstructionSiteRequest {
  const request: ConstructionSiteRequest = {};
  for (const field of CONSTRUCTION_SITE_FORM_FIELDS) {
    const trimmed = value[field].trim();
    if (trimmed) request[field] = trimmed;
  }
  return request;
}

/**
 * Patch body: only the fields whose value actually changed against `original`.
 * A field the user cleared is NOT sent — `null`/omitted mean "do not change" on
 * this endpoint, so clearing is impossible; sending `''` instead would either be
 * ignored or fail the minimum-length check.
 */
export function toUpdateRequest(
  value: ConstructionSiteFormValue,
  original: ConstructionSiteFormValue
): ConstructionSiteRequest {
  const request: ConstructionSiteRequest = {};
  for (const field of CONSTRUCTION_SITE_FORM_FIELDS) {
    const trimmed = value[field].trim();
    if (!trimmed || trimmed === original[field].trim()) continue;
    request[field] = trimmed;
  }
  return request;
}

/** Whether a patch would change anything — drives the disabled save button. */
export function hasConstructionSiteChanges(
  value: ConstructionSiteFormValue,
  original: ConstructionSiteFormValue
): boolean {
  return Object.keys(toUpdateRequest(value, original)).length > 0;
}

/**
 * Form prefill for an existing construction site, read back from
 * `GET /customer-service/customer/{constructionSiteId}`.
 *
 * The customer response is a superset in some places and a subset in others: the
 * address and contact values live on `mainContact` (as everywhere else in this
 * panel), the Kurzbezeichnung on `account.name`. Several request-only fields have
 * no counterpart in the GET and stay empty.
 *
 * TODO: prefill streetNumber, poBox, clerk1-3, costCenter, federalOrderReference,
 *       deletionDate, bankAccount, deliveryHint and customerDeliveryHint once the
 *       customer GET exposes them — the PATCH diff never sends untouched fields,
 *       so an unfilled field is left alone rather than overwritten.
 */
export function toFormValue(
  customer: CustomerResponse | null | undefined
): ConstructionSiteFormValue {
  if (!customer) return { ...EMPTY_CONSTRUCTION_SITE_FORM_VALUE };
  const contact = customer.mainContact;
  const text = (value: string | null | undefined): string => value?.trim() ?? '';
  return {
    ...EMPTY_CONSTRUCTION_SITE_FORM_VALUE,
    name: text(customer.account?.name),
    salutation: text(contact?.salutation),
    name1: text(contact?.lastName),
    name2: text(contact?.firstName),
    name3: text(contact?.nameAffix),
    street: text(contact?.street),
    countryCode: text(contact?.countryCode),
    postCode: text(contact?.zipCode),
    city: text(contact?.location),
    telephoneNumber: text(contact?.telephoneNumberOrganisation),
    fax: text(contact?.faxOrganisation),
    mail1: text(contact?.mail1),
    vatId: text(customer.vatId),
    representativeId: text(customer.representative?.id),
  };
}
